import ExcelJS from 'exceljs';
import { resolvePhotoDataUrl } from './photoStorage';
import { downloadBlob, sanitizeFilename } from './downloadFile';
import { formatContactValue } from './contactValue';
import { getFieldDisplayName } from './formSchemaUtils';
import { getSchemaEntries, getSchemaFields, isSection, sectionFields, sectionTitle } from './formSections';
import { getFormHeader } from './formHeader';
import { formatSelectValue } from './selectValue';

const formatAnswerForCell = (field, value, calculatedValues) => {
  if (field.type === 'calculation') {
    const calc = calculatedValues?.[field.id];
    if (calc !== undefined && calc !== '') return calc;
    return value ?? '';
  }
  if (field.type === 'contact') {
    return formatContactValue(value);
  }
  if (field.type === 'select') {
    // Multi-answer dropdowns hold a list; print it in the author's option order.
    return formatSelectValue(value, field.options);
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value ?? '';
};

const parsePhotoDataUrl = (value) => {
  if (typeof value !== 'string') return null;
  const match = /^data:image\/(png|jpeg|jpg|gif);base64,(.+)$/i.exec(value);
  if (!match) return null;
  const ext = match[1].toLowerCase() === 'jpg' ? 'jpeg' : match[1].toLowerCase();
  return { extension: ext, base64: value };
};

// Report-header geometry. The sheet is two columns wide (32 + 60 characters);
// the logo is anchored by fractional column so it lands at the left edge, the
// middle, or the right edge of that span, matching the position the author
// picked in the builder.
const FIELD_COLUMN_WIDTH = 32;
const VALUE_COLUMN_WIDTH = 60;
const LOGO_SIZE_PX = 72;
const LOGO_ROW_HEIGHT = 58;
const TEXT_LINE_HEIGHT = 15;
const LOGO_ANCHOR_COL = { left: 0.08, center: 1.15, right: 1.78 };

// Excel clips a fixed-height row, so grow the header row to fit however many
// lines the text wraps into.
const estimateWrappedLines = (text, columnWidth) =>
  text
    .split('\n')
    .reduce((lines, line) => lines + Math.max(1, Math.ceil(line.length / columnWidth)), 0);

const writeHeaderText = (cell, text, horizontal) => {
  cell.value = text;
  cell.font = { bold: true, size: 12 };
  cell.alignment = { wrapText: true, vertical: 'middle', horizontal };
};

// Draw the form's report header above the answers: the author's text plus an
// optional logo on the side they chose. Returns with the cursor left on a blank
// spacer row, or does nothing at all when the form has no header.
const addHeaderBlock = (workbook, sheet, header, logoDataUrl) => {
  const text = (header.text || '').trim();
  const logo = parsePhotoDataUrl(logoDataUrl);
  if (!text && !logo) return;

  const imageId = logo
    ? workbook.addImage({ base64: logo.base64, extension: logo.extension })
    : null;
  const anchorCol = LOGO_ANCHOR_COL[header.logoPosition] ?? LOGO_ANCHOR_COL.right;
  const placeLogo = (rowNumber) => {
    if (imageId === null) return;
    sheet.addImage(imageId, {
      tl: { col: anchorCol, row: rowNumber - 1 },
      ext: { width: LOGO_SIZE_PX, height: LOGO_SIZE_PX },
    });
  };
  const sizeRow = (row, lines, hasLogo) => {
    row.height = Math.max(hasLogo ? LOGO_ROW_HEIGHT : 0, lines * TEXT_LINE_HEIGHT);
  };

  // Centered: the logo gets its own row so the text can sit centred beneath it.
  if (imageId !== null && header.logoPosition === 'center') {
    const logoRow = sheet.addRow([]);
    logoRow.height = LOGO_ROW_HEIGHT;
    placeLogo(logoRow.number);
    if (text) {
      const textRow = sheet.addRow([]);
      sheet.mergeCells(textRow.number, 1, textRow.number, 2);
      writeHeaderText(textRow.getCell(1), text, 'center');
      sizeRow(textRow, estimateWrappedLines(text, FIELD_COLUMN_WIDTH + VALUE_COLUMN_WIDTH), false);
    }
  } else {
    const row = sheet.addRow([]);
    // Text goes in the column opposite the logo so the two never overlap.
    const usesValueColumn = imageId !== null && header.logoPosition === 'left';
    const textColumn = usesValueColumn ? 2 : 1;
    const textWidth = imageId === null
      ? FIELD_COLUMN_WIDTH + VALUE_COLUMN_WIDTH
      : (usesValueColumn ? VALUE_COLUMN_WIDTH : FIELD_COLUMN_WIDTH);
    if (text) {
      if (imageId === null) sheet.mergeCells(row.number, 1, row.number, 2);
      writeHeaderText(row.getCell(textColumn), text, 'left');
    }
    sizeRow(row, text ? estimateWrappedLines(text, textWidth) : 0, imageId !== null);
    placeLogo(row.number);
  }

  sheet.addRow([]);
};

// A section's name, written across both columns as a banner above what it
// holds, so the sheet reads in the same chapters as the form. A subsection gets
// a lighter band and an indent, the way it is drawn on the page — the sheet is
// flat, so the shade and the indent are all that carry the nesting.
const SECTION_FILLS = ['FF4C1D95', 'FF5B21B6', 'FF6D28D9'];

const sectionFill = (depth) => SECTION_FILLS[Math.min(depth, SECTION_FILLS.length - 1)];

const addSectionRow = (sheet, section, depth) => {
  const row = sheet.addRow([]);
  sheet.mergeCells(row.number, 1, row.number, 2);
  const cell = row.getCell(1);
  cell.value = sectionTitle(section);
  cell.font = { bold: true, size: depth === 0 ? 12 : 11, color: { argb: 'FFFFFFFF' } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: sectionFill(depth) } };
  cell.alignment = { vertical: 'middle', indent: depth };
};

// Build a two-column (Field / Value) workbook for a single submission, under the
// form's report header, with each section announced by a banner row above what
// it holds — subsections included — and photo answers embedded as images.
// Questions and sections in `hiddenIds` — those whose conditions the answers
// didn't meet — are left out, the way they are on screen. Returns null when the
// form has no questions.
export const buildSubmissionWorkbook = async ({
  schema,
  formData,
  calculatedValues,
  hiddenIds = new Set(),
}) => {
  if (!getSchemaFields(schema).length) return null;

  // Photo answers and the header logo are stored as references; resolve them to
  // data URLs (fetching from storage if needed) up front so the synchronous row
  // build can embed them.
  const header = getFormHeader(schema);
  const photoFields = getSchemaFields(schema).filter((f) => f.type === 'photo' && !hiddenIds.has(f.id));
  const [resolvedPhotos, headerLogo] = await Promise.all([
    Promise.all(photoFields.map((f) => resolvePhotoDataUrl(formData[f.id]).catch(() => null))),
    header.logo ? resolvePhotoDataUrl(header.logo).catch(() => null) : Promise.resolve(null),
  ]);
  const photoByFieldId = {};
  photoFields.forEach((f, i) => {
    photoByFieldId[f.id] = resolvedPhotos[i];
  });

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Submission');
  // Widths only — the Field/Value titles are written as a row below the header
  // block, which has to come first.
  sheet.columns = [
    { key: 'field', width: FIELD_COLUMN_WIDTH },
    { key: 'value', width: VALUE_COLUMN_WIDTH },
  ];

  addHeaderBlock(workbook, sheet, header, headerLogo);
  sheet.addRow({ field: 'Field', value: 'Value' }).font = { bold: true };

  const addFieldRow = (field, depth) => {
    // Field name when the author gave one, otherwise the question text.
    const columnA = getFieldDisplayName(field);
    const rawValue = formData[field.id];
    const row = sheet.addRow({ field: columnA, value: '' });
    const valueCell = row.getCell('value');
    valueCell.alignment = { wrapText: true, vertical: 'top' };
    row.getCell('field').alignment = { vertical: 'top', indent: depth };

    const photo = field.type === 'photo' ? parsePhotoDataUrl(photoByFieldId[field.id]) : null;
    if (photo) {
      const imageId = workbook.addImage({
        base64: photo.base64,
        extension: photo.extension,
      });
      row.height = 120;
      sheet.addImage(imageId, {
        tl: { col: 1, row: row.number - 1 },
        ext: { width: 160, height: 160 },
      });
    } else if (field.type === 'photo') {
      // Photo field with no resolvable image — leave the cell blank rather than
      // printing the raw reference string.
      valueCell.value = '';
    } else {
      valueCell.value = formatAnswerForCell(field, rawValue, calculatedValues);
    }
  };

  const addEntries = (entries, depth) => {
    entries.forEach((entry) => {
      if (hiddenIds.has(entry.id)) return;
      if (!isSection(entry)) {
        addFieldRow(entry, depth);
        return;
      }
      addSectionRow(sheet, entry, depth);
      addEntries(sectionFields(entry), depth + 1);
    });
  };

  addEntries(getSchemaEntries(schema), 0);

  return workbook;
};

// Build the workbook for a submission and hand it to the browser as a download.
export const exportSubmissionToXlsx = async ({
  schema,
  formData,
  calculatedValues,
  hiddenIds,
  submission,
  form,
}) => {
  const workbook = await buildSubmissionWorkbook({ schema, formData, calculatedValues, hiddenIds });
  if (!workbook) return;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, `${sanitizeFilename(submission.title || form.name)}-${submission.id}.xlsx`);
};
